import React, { useEffect } from "react";
import { connect } from "react-redux";
import { Form, message, Select, Button } from "antd";
import { Location } from "assets/icons";
import { bindActionCreators } from "redux";
import { Creators as LocationActions } from "store/location/location-duck";
import { ILocationData } from "store/location/location-model";
import { Creators as MultipleRealmsActions } from "store/multiple-realms/multiple-realms-duck";
import { ILoginIn } from "typings/auth.types";
import useTranslation from "utils/hooks/useTranslation";
import * as S from "./styles/styled";

interface IPropsScreenLogin {
  locationActiveList?: ILocationData[];
  disabledRegisterButton?: boolean;
  isLoading?: boolean;
}
interface IDispatchProps {
  toggleUnregisteredOpen: () => void;
  userSessionShowLoading?: (...args) => void;
  userSessionHideLoading?: (...args) => void;
  userSessionLogout?: (...args) => void;
  userSessionUpdateUserData?: (...args) => void;
  locationActiveFetchPublicAsync?: (...args) => void;
  multipleRealmsSaveUserInfoAsync?: (...args) => void;
}

export type IPropsLoginPage = IPropsScreenLogin & IDispatchProps;

const UnregisteredInfo = ({
  isLoading,
  locationActiveList,
  disabledRegisterButton,
  toggleUnregisteredOpen,
  locationActiveFetchPublicAsync,
  multipleRealmsSaveUserInfoAsync,
}: IPropsLoginPage) => {
  const IAM_ALREADY_REGISTERED = useTranslation({ id: "iam_already_registered" });
  const LANGUAGE = useTranslation({ id: "language" });
  const FIRST_ACCESS = useTranslation({ id: "first_access" });
  const REQUIRED_FIELD = useTranslation({ id: "required_field" });
  const ERROR_MESSAGE = useTranslation({ id: "required_fields_are_required" });
  const FULL_NAME = useTranslation({ id: "full_name" });
  const INSERT_YOUR_FULL_NAME = useTranslation({ id: "insert_your_full_name" });
  const INSERT_YOUR_EMAIL = useTranslation({ id: "insert_your_email" });
  const SELECT_YOUR_UNIT = useTranslation({ id: "select_your_unit" });
  const SELECT_YOUR_LANGUAGE = useTranslation({ id: "select_your_language" });
  const DESCRIPTION_TITLE_FIRST_ACCESS_FORM = useTranslation({ id: "description_title_first_access_form" });
  const UNIT = useTranslation({ id: "unit" });
  const REGISTER = useTranslation({ id: "register" });
  const [form] = Form.useForm();
  const languageOptions = ["pt-BR", "es-MX", "en-US"];

  useEffect(() => {
    locationActiveFetchPublicAsync();
  }, []);

  const onFinish = () => {
    form
      .validateFields()
      .then(({ location, fullName, email, language }: ILoginIn) => {
        const getLocation = locationActiveList.find((item) => item.id === location);
        multipleRealmsSaveUserInfoAsync(getLocation.id, getLocation.description, fullName, email, language);
      })
      .catch(() => {
        message.error(ERROR_MESSAGE);
      });
  };

  const iconLabel = (description) => (
    <React.Fragment>
      <Location style={S.LocationIcon} />
      {description}
    </React.Fragment>
  );

  return (
    <S.SSOContainer>
      <S.ContainerForm>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            location: [],
          }}
          data-testid="form"
        >
          <>
            <S.TitleDescriptionContainer>
              <S.Greetings>{FIRST_ACCESS}</S.Greetings>
              <span>{DESCRIPTION_TITLE_FIRST_ACCESS_FORM}</span>
            </S.TitleDescriptionContainer>
            <S.FormContainer>
              <S.FormItem
                name="fullName"
                hasFeedback
                rules={[
                  {
                    required: true,
                    message: REQUIRED_FIELD,
                  },
                ]}
                label={`${FULL_NAME}*`}
              >
                <S.InputRole maxLength="200" allowClear placeholder={INSERT_YOUR_FULL_NAME} />
              </S.FormItem>
              <S.FormItem
                name="email"
                hasFeedback
                rules={[
                  {
                    required: true,
                    message: REQUIRED_FIELD,
                  },
                ]}
                label="Email*"
              >
                <S.InputRole maxLength="100" allowClear placeholder={INSERT_YOUR_EMAIL} />
              </S.FormItem>
              <S.FormItem
                name="location"
                hasFeedback
                rules={[
                  {
                    required: true,
                    message: REQUIRED_FIELD,
                  },
                ]}
                label={`${UNIT}*`}
              >
                <Select
                  key="location"
                  showSearch
                  loading={isLoading}
                  optionFilterProp="children"
                  filterOption={(input, option) => option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                  size="large"
                  optionLabelProp="label"
                  placeholder={
                    <React.Fragment>
                      <Location style={S.LocationIcon} />
                      {SELECT_YOUR_UNIT}
                    </React.Fragment>
                  }
                >
                  {locationActiveList?.length > 1 &&
                    locationActiveList.map((option) => (
                      <Select.Option value={option.id} key={option.id} label={iconLabel(option.description)}>
                        {option.description}
                      </Select.Option>
                    ))}
                </Select>
              </S.FormItem>
              <S.FormItem
                name="language"
                hasFeedback
                rules={[
                  {
                    required: true,
                    message: REQUIRED_FIELD,
                  },
                ]}
                label={`${LANGUAGE}*`}
              >
                <Select key="language" optionFilterProp="children" size="large" optionLabelProp="label" placeholder={SELECT_YOUR_LANGUAGE}>
                  {languageOptions.map((option) => (
                    <Select.Option value={option} key={option}>
                      {option}
                    </Select.Option>
                  ))}
                </Select>
              </S.FormItem>
            </S.FormContainer>
            <S.FormItem>
              <S.SSOContainerButtonsContainer>
                <Button className="primaryButton" htmlType="submit" size="large" data-testid="submit" loading={disabledRegisterButton}>
                  {REGISTER}
                </Button>
                <Button className="secondaryButton" onClick={toggleUnregisteredOpen} size="large" data-testid="back" disabled={disabledRegisterButton}>
                  {IAM_ALREADY_REGISTERED}
                </Button>
              </S.SSOContainerButtonsContainer>
            </S.FormItem>
          </>
        </Form>
      </S.ContainerForm>
    </S.SSOContainer>
  );
};

const mapDispatchToActions = (dispatch: any) => bindActionCreators({ ...LocationActions, ...MultipleRealmsActions }, dispatch);

const mapStateToProps = (state: any) => ({
  locationActiveList: state.Location.locationActiveList,
  isLoading: state.Location.isLoading,
  disabledRegisterButton: state.MultipleRealms.disabledRegisterButton,
});

export default connect(mapStateToProps, mapDispatchToActions)(UnregisteredInfo);
